import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import DashboardIcon from '@avila-tek/ui/src/icons/DashboardIcon';
import HeartIcon from '@avila-tek/ui/src/icons/HeartIcon';
import UserIcon from '@avila-tek/ui/src/icons/UserIcon';
import MenuIcon from '@avila-tek/ui/src/icons/MenuIcon';
import ShoppingCarIcon from '@avila-tek/ui/src/icons/ShoppingCarIcon';
import TriangleIcon from '@avila-tek/ui/src/icons/TriangleIcon';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { Loading, LogoutIcon } from '@avila-tek/ui/src';
import { signOut, useSession } from 'next-auth/react';
import { GET_CATEGORIES } from '../../graphql/queries';
import { Category } from '../../models';
import {
  useClientPoints,
  useCompanyBranding,
  useNotify,
  useUser,
} from '../../hooks';
import { formatIntegerNumber } from '../../lib/FormatedClientsPoints';
import { useShopCart } from '../../hooks/useShopCart';
import Button from '../shared/Button/Button';
import HomeImage from '../home/homeBanners/HomeImage';

function Navbar({ openClose }) {
  const notify = useNotify();
  const router = useRouter();
  const [user] = useUser();
  const { data: session } = useSession();
  const [companyBranding] = useCompanyBranding();
  const { points } = useClientPoints();
  const [shopCart] = useShopCart();

  const {
    data: dataCategories,
    loading,
    error,
  } = useQuery<{
    categories: Category[];
  }>(GET_CATEGORIES, {
    fetchPolicy: 'cache-and-network',
    variables: {
      filter: { active: true },
      sort: 'NAME_ASC',
    },
  });

  const handleSignOut = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      e.preventDefault();
      localStorage.removeItem('x-token-client');
      await signOut({ callbackUrl: `/sign-in` });
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  return (
    <nav className="bg-system-100 relative border-b-[8px] border-color-secondary-200 z-50">
      <div className="h-20 w-full flex flex-row justify-between items-center px-5 lg:px-16">
        {companyBranding?.logo?.image ? (
          <Link href="/sections" className="bg-white hidden md:flex">
            <HomeImage
              bannerImage={companyBranding?.logo?.image}
              fallbackSrc="image/banner"
              className="absolute top-[55%] right-[87%] transform translate-x-1/2 -translate-y-1/2"
              altText="Company Logo"
            />
          </Link>
        ) : (
          <Link href="/sections" className="bg-white hidden md:flex">
            <Image
              alt="logo"
              src="/images/logo-meit.png"
              height={60}
              width={160}
            />
          </Link>
        )}
        <div className="block lg:hidden">
          <Button
            id="button"
            className="bg-white flex items-center px-3 py-2 border rounded text-color-primary-text-300 border-color-primary-300 "
            onClick={openClose}
          >
            <MenuIcon />
          </Button>
        </div>

        <div className="flex flex-row items-center gap-2">
          <div className="h-auto w-full flex-row relative items-center hidden lg:flex xl:flex ">
            <div className="relative group w-40 hover:bg-neutral-200">
              {session?.user ? (
                <div className="w-full rounded-md p-2 bg-system-100 hover:bg-secondary-200 transition ease-in-out duration-300 text-color-primary-text-300 text-xsm text-center flex justify-center items-center border border-color-primary-300 ">
                  <div className="flex justify-center pr-2">
                    <DashboardIcon className="w-4 text-color-primary-text-300 grid content-center" />
                  </div>
                  <div className="h-full w-38 lg:w-full px-2 md:px-0 text-center flex justify-center items-center">
                    Catálogo
                  </div>
                </div>
              ) : null}
              <section className="md:absolute z-50 hidden group-hover:block p-3 lg:rounded-md md:rounded-md">
                <div className="absolute -left-3 -top-2 mt-2 min-w-[250px] min-h-[250px] max-h-[340px] bg-white rounded-lg shadow-lg">
                  <ul className="py-2">
                    <Link href="/sections" as="/sections">
                      <li className="gap-y-4">
                        <h1 className="px-4 py-4 font-bold text-lg hover:bg-gray-100 cursor-pointer">
                          Todas las categorías
                        </h1>
                      </li>
                    </Link>
                    <div className="overflow-y-auto max-h-[250px]">
                      {loading ? (
                        <Loading />
                      ) : (
                        dataCategories?.categories?.map((category) => (
                          <Link
                            href={`/category/${category?._id}`}
                            as={`/category/${category?._id}`}
                            key={category?._id}
                          >
                            <li
                              key={category?._id}
                              className="gap-y-4 border-b hover:bg-gray-100 cursor-pointer p-2"
                            >
                              <p className="ml-2">{category?.name}</p>
                            </li>
                          </Link>
                        ))
                      )}
                    </div>
                  </ul>
                </div>
              </section>
            </div>
          </div>
          {user ? (
            <div className="ml-5">
              <div className="flex flew-row bg-system-100 rounded-lg items-center gap-4">
                <Link href="/wish-list">
                  <div className="bg-system-100 hover:bg-neutral-200 border-solid border border-system-500 flex items-center justify-center rounded-md py-2 px-3">
                    <HeartIcon className="flex justify-center text-neutral-400 w-6" />
                  </div>
                </Link>
                <Link href="/shopping">
                  <div className="relative bg-system-100 hover:bg-neutral-200 border-solid border border-system-500 flex items-center justify-center rounded-md py-2 px-3">
                    <ShoppingCarIcon className="flex justify-center text-neutral-400 w-6" />
                    {shopCart?.products?.length > 0 && (
                      <div className="absolute -top-1 -right-1 bg-color-primary-300 text-system-100 rounded-full h-4 w-4 flex items-center justify-center px-[2px] text-xs">
                        {shopCart?.products?.length > 9 ? (
                          <span className="text-[10px]">9+</span>
                        ) : (
                          <span className="text-xs">
                            {shopCart?.products?.length}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
                <Link href="/my-profile" className="hidden m_sm:flex">
                  <div className="bg-system-100 hover:bg-neutral-200 border-solid border border-system-500 items-center justify-center rounded-md py-2 px-3 text-neutral-400 space-x-2 ">
                    <UserIcon className="flex justify-center text-neutral-400 w-6" />
                  </div>
                </Link>
                <div className="hidden lg:flex bg-system-100 hover:bg-neutral-200 border-solid border border-system-500 items-center justify-center rounded-md py-2 px-3">
                  <h1 className="text-nowrap text-neutral-400">
                    <span className="font-bold">
                      {formatIntegerNumber(points)}
                    </span>{' '}
                    PUNTOS
                  </h1>
                </div>
                <button
                  className="bg-system-100 hover:bg-neutral-200 border-solid border border-system-500 items-center justify-center rounded-md py-2 px-3 "
                  type="button"
                  onClick={handleSignOut}
                >
                  <LogoutIcon className="flex justify-center text-neutral-400 w-6" />
                  <span className="sr-only">Sign out</span>
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="py-2 rounded tracking-wide transition-colors w-fit whitespace-nowrap px-10 bg-color-primary-300 text-system-100 hover:bg-color-primary-200 font-normal border-0"
              onClick={() => router.push('/sign-in')}
            >
              Iniciar Sesión
            </button>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
